<template>
  <div class="edit-wrapper container pb-3 mt-4">
    <!-- Header + Dropdown filter -->
    <div class="d-flex flex-column flex-md-row justify-content-end align-items-center gap-3 mb-4" >
      <div style="min-width: 250px;">
        <select
            class="form-select rounded-pill shadow-sm px-4 py-2 border-0 custom-select-arrow"
            v-model="selectedCandidateType"
            @change="onTypeChange"
        >
          <option
              v-for="type in filteredTypes"
              :key="type._id"
              :value="type._id"
          >
            {{ type.type_name }}
          </option>
        </select>
      </div>
    </div>

    <!-- Edit header -->
    <div class="text-center mb-4">
      <template v-if="currentCondition">
        <div v-if="!isEditing">
          <h4 class="fw-bold mb-3">{{ headerText }}</h4>
          <label
              class="custom-file-upload fw-semibold btn btn-outline-secondary"
              style="cursor: pointer; display: inline-block;"
          >
            <input type="button" @click="isEditing = true" style="display: none;" />
            ✏️ Edit
          </label>
        </div>

        <div v-else>
          <input
              v-model="editableHeader"
              class="form-control form-control-lg text-center mx-auto"
              placeholder="Enter Header Text"
              style="max-width: 600px;"
          />
          <button
              class="btn btn-primary mt-3 px-5 fw-bold"
              @click="saveHeader"
          >
            💾 Save
          </button>
        </div>
      </template>

      <p v-else class="text-danger fw-bold">
        ⚠️ No condition found for this type.
      </p>
    </div>

    <!-- Preview Images -->
    <div class="row justify-content-center g-4 mb-5" v-if="currentCondition">
      <div
          class="col-12 col-md-4"
          v-for="img in currentCondition.condition_imgs"
          :key="img"
      >
        <div class="image-card position-relative p-2 rounded shadow-sm bg-white">
          <img :src="fullImageUrl(img)" class="w-100 rounded condition-image" alt="Uploaded Image" />
          <button
              class="btn btn-sm btn-danger position-absolute top-0 end-0 m-2"
              @click="removeImage(img)"
          >
            &times;
          </button>
        </div>
      </div>
    </div>

    <!-- Upload Image -->
    <div class="text-center mt-3 mb-4" v-if="currentCondition">
      <label class="custom-file-upload">
        <input type="file" @change="handleImageUpload" accept="image/*" multiple />
        📁 Choose Images
      </label>
    </div>
  </div>
</template>

<script>
import api from '@/service/api.js'
import Swal from 'sweetalert2'
import '@/assets/css/condition.css'

export default {
  name: 'edit_union',
  data() {
    return {
      isEditing: false,
      headerText: '',
      editableHeader: '',
      types: [],
      conditions: [],
      selectedCandidateType: null,
      currentCondition: null,
      imageBaseURL: 'https://election-su.mfu.ac.th' // ✅ base URL ของ server
    }
  },
  computed: {
    filteredTypes() {
      return this.types.filter(type => type._id !== '686bad88266df45e88c9ca62')
    }
  },
  methods: {
    fullImageUrl(img) {
      // คืนค่าพร้อม baseURL
      return `${this.imageBaseURL}${img}`
    },
    async fetchTypes() {
      const { data } = await api.type("get")
      this.types = data.data || []
    },
    async fetchConditions() {
      const { data } = await api.condition("get")
      this.conditions = data.data || []
    },
    onTypeChange() {
      if (!this.conditions.length) return

      this.currentCondition = this.conditions.find(cond => {
        let condType = cond.type
        if (typeof condType === 'object' && condType._id) {
          condType = condType._id
        }
        return String(condType) === String(this.selectedCandidateType)
      })

      if (this.currentCondition) {
        this.headerText = this.currentCondition.condition_header
        this.editableHeader = this.currentCondition.condition_header
      } else {
        this.headerText = ''
        this.editableHeader = ''
        this.currentCondition = null
      }
      this.isEditing = false
    },
    async handleImageUpload(event) {
      const files = event.target.files
      if (!files.length || !this.currentCondition?._id) return

      const validImageTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp']
      for (const file of files) {
        if (!validImageTypes.includes(file.type)) {
          Swal.fire({
            icon: 'error',
            title: 'Invalid File Type',
            text: 'Only image files are allowed (jpg, png, gif, webp).',
            confirmButtonText: 'OK'
          })
          event.target.value = '' // reset file input
          return
        }
      }

      try {
        await api.condition('putConditionPictures', {
          id: this.currentCondition._id,
          images: files
        })

        Swal.fire({
          icon: 'success',
          title: 'Images Uploaded',
          text: `${files.length} image(s) uploaded successfully.`,
          timer: 2000,
          showConfirmButton: false
        })

        await this.fetchConditions()
        this.onTypeChange()
      } catch (err) {
        Swal.fire('Error', err.response?.data?.message || err.message, 'error')
      }
    },
    async removeImage(imagePath) {
      if (!this.currentCondition?._id) return

      try {
        await api.condition('deleteConditionPicture', {
          id: this.currentCondition._id,
          imagePath
        })

        Swal.fire({
          icon: 'success',
          title: 'Image Deleted',
          text: 'The image has been deleted.',
          timer: 2000,
          showConfirmButton: false
        })

        await this.fetchConditions()
        this.onTypeChange()
      } catch (err) {
        Swal.fire('Error', err.response?.data?.message || err.message, 'error')
      }
    },
    async saveHeader() {
      if (!this.selectedCandidateType) {
        return Swal.fire('Error', 'Please select a candidate type.', 'error')
      }

      try {
        const payload = {
          _id: this.currentCondition?._id,
          condition_header: this.editableHeader,
          type: this.selectedCandidateType
        }
        await api.condition('put', payload)

        this.isEditing = false

        await this.fetchConditions()
        this.onTypeChange()

        Swal.fire({
          icon: 'success',
          title: 'Saved!',
          text: 'Condition header updated.',
          confirmButtonText: 'OK'
        })
      } catch (err) {
        Swal.fire('Error', err.response?.data?.message || err.message, 'error')
      }
    }
  },
  async mounted() {
    await this.fetchTypes()
    await this.fetchConditions()

    const defaultType = this.types.find(t => t.type_name === 'President of the Student Union')
    if (defaultType) {
      this.selectedCandidateType = defaultType._id
      this.onTypeChange()
    }
  }
}
</script>
